import { captureException } from '@sentry/react';
import {
  type QueryClient,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';

import {
  ItemModel,
  itemClient,
  UpdateItemStatusModel,
} from '@merchant-clients/item';

import { useToast } from '@frontend/components';
import { useClientQuery } from '@frontend/hooks';
import { tApp, tItem } from '@frontend/locales';

const fetchItems = async (shopId: string) => {
  return itemClient.list(shopId);
};

export const getItemsQueryKey = (shopId: string) => ['items', shopId];

export interface UseItemsQueryProps {
  shopId: string;
  onError?(error: Error): void;
}

export const useItemsQuery = ({ shopId, onError }: UseItemsQueryProps) => {
  return useClientQuery({
    queryKey: getItemsQueryKey(shopId),
    enabled: !!shopId,
    async onFetch() {
      return await fetchItems(shopId);
    },
    onError,
  });
};

const updateCachedItem = (
  queryClient: QueryClient,
  shopId: string,
  key: string,
  updateFn: (existingItem: ItemModel) => ItemModel,
) => {
  queryClient.setQueryData<ItemModel[]>(getItemsQueryKey(shopId), (oldData) => {
    if (!oldData) {
      return oldData;
    }

    return oldData.map((item) => (item.key !== key ? item : updateFn(item)));
  });
};

export interface UpdateItemStatusVariables {
  key: string;
  status: UpdateItemStatusModel['status'];
}

export const useUpdateItemStatusMutation = (shopId: string) => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation<ItemModel, Error, UpdateItemStatusVariables>({
    mutationFn: async ({ key, status }) => {
      const existingItems = queryClient.getQueryData<ItemModel[]>(
        getItemsQueryKey(shopId),
      );
      const existingItem = existingItems?.find((item) => item.key === key);

      updateCachedItem(queryClient, shopId, key, (item) => ({
        ...item,
        status,
      }));

      return await itemClient.updateStatus(key, { status }).catch((error) => {
        if (existingItem) {
          // The self-service update endpoint does not resolve categories for
          // the updated item, so we only ever patch the `status` field into
          // the cache instead of replacing the whole cached item.
          updateCachedItem(queryClient, shopId, key, () => existingItem);
        }

        throw error;
      });
    },
    onSuccess: (updatedItem, { key }) => {
      updateCachedItem(queryClient, shopId, key, (item) => ({
        ...item,
        status: updatedItem.status,
      }));
    },
    onError(error, { key }) {
      showToast({
        header: tItem.errors.statusUpdateError(),
        body: tApp.errorPages.unexpectedErrorDescription(),
        severity: 'error',
      });
      captureException(error, {
        level: 'error',
        extra: {
          message: `Failed to update status for item with key ${key}`,
        },
      });
    },
  });
};
